import { __rest, __assign } from 'tslib';
import React, { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import { useMaskito } from '@maskito/react';
import cn from 'classnames';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import { HOURS_MINUTES_SEPARATOR, DATE_RANGE_SEPARATOR, DEFAULT_MIN_DATE, DEFAULT_MAX_DATE } from '../../consts.js';
import { createMaskOptions } from '../../mask/index.js';
import { DateInput } from '../date-input/Component.js';
import { DateRangeInput } from '../date-range-input/Component.js';
import { TimeInput } from '../time-input/Component.js';
import { s as styles } from '../../index.module-bbbccbbb.js';
import 'date-fns/clamp';
import 'date-fns/getDaysInMonth';
import 'date-fns/isValid';
import 'date-fns/differenceInDays';
import 'date-fns/format';
import 'date-fns/isSameDay';
import 'date-fns/parse';
import '../../../../input/esm';
import '../../../../popover/esm';
import '../../../../shared/esm';
import '@alfalab/icons-glyph/CalendarMIcon';
import 'date-fns/startOfMonth';
import '../../mask/plugins.js';
import '../../mask/utils.js';
import '../../mask/postprocessors.js';
import '../../mask/preprocessors.js';
import '../../utils/index.js';

var BaseUniversalDateInput = forwardRef(function (_a, ref) {
    var _b;
    var _c = _a.autoCorrection, autoCorrection = _c === void 0 ? true : _c, _d = _a.minDate, minDate = _d === void 0 ? DEFAULT_MIN_DATE : _d, _e = _a.maxDate, maxDate = _e === void 0 ? DEFAULT_MAX_DATE : _e, view = _a.view, picker = _a.picker, onCalendarClose = _a.onCalendarClose, onCalendarOpen = _a.onCalendarOpen, platform = _a.platform, onKeyDown = _a.onKeyDown, disabled = _a.disabled, readOnly = _a.readOnly, fieldClassName = _a.fieldClassName, disableUserInput = _a.disableUserInput, restProps = __rest(_a, ["autoCorrection", "minDate", "maxDate", "view", "picker", "onCalendarClose", "onCalendarOpen", "platform", "onKeyDown", "disabled", "readOnly", "fieldClassName", "disableUserInput"]);
    var _f = useState(false), correctionOccurred = _f[0], setCorrectionOccurred = _f[1];
    var _g = useState(false), open = _g[0], setOpen = _g[1];
    var inputRef = useRef(null);
    var calendarRef = useRef(null);
    var inputWrapperRef = useRef(null);
    var maskOptions = useMemo(function () {
        return createMaskOptions(view, startOfDay(minDate), endOfDay(maxDate), autoCorrection, handleCorrection);
    }, [view, minDate, maxDate, autoCorrection]);
    var maskRef = useMaskito({ options: maskOptions });
    useEffect(function () {
        var timeoutId;
        if (correctionOccurred) {
            timeoutId = setTimeout(function () { return setCorrectionOccurred(false); }, 150);
        }
        return function () { return clearTimeout(timeoutId); };
    }, [correctionOccurred]);
    var openCalendar = function () {
        if (picker) {
            setOpen(function (p) {
                if (!p)
                    onCalendarOpen === null || onCalendarOpen === void 0 ? void 0 : onCalendarOpen();
                return true;
            });
        }
    };
    var closeCalendar = function () {
        if (picker) {
            setOpen(function (p) {
                if (p)
                    onCalendarClose === null || onCalendarClose === void 0 ? void 0 : onCalendarClose();
                return false;
            });
        }
    };
    function handleCorrection() {
        setCorrectionOccurred(true);
    }
    var handleKeyDown = function (event) {
        if (event.key === 'Escape')
            closeCalendar();
    };
    var handleInputKeyDown = function (event) {
        if (['ArrowDown', 'ArrowUp'].includes(event.key) && calendarRef.current) {
            event.preventDefault();
            calendarRef.current.focus();
        }
        if (event.key === 'Enter') {
            if (open)
                closeCalendar();
            else
                openCalendar();
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    };
    var handleClick = function (event) {
        var _a, _b;
        if (!((_a = inputWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)))
            return;
        if (platform === 'desktop') {
            if (!open)
                openCalendar();
            (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        }
        if (platform === 'mobile' && disableUserInput && !open) {
            openCalendar();
        }
    };
    var handlePickerClick = function () {
        if (platform === 'mobile' && !disableUserInput && !open) {
            openCalendar();
        }
    };
    var handleFocus = function (event) {
        var _a;
        if (!((_a = inputWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)))
            return;
        if (platform === 'desktop')
            openCalendar();
    };
    var handleBlur = function (event) {
        var _a;
        if (platform === 'desktop') {
            var target = (event.relatedTarget || document.activeElement);
            if (inputRef.current !== target &&
                ((_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) === false) {
                closeCalendar();
            }
        }
    };
    var inputDisabled = disabled || readOnly;
    var commonProps = {
        autoComplete: picker ? 'off' : undefined,
        disableUserInput: disableUserInput,
        autoCorrection: autoCorrection,
        disabled: disabled,
        readOnly: readOnly,
        fieldClassName: cn(fieldClassName, (_b = {}, _b[styles.blink] = correctionOccurred, _b)),
        onKeyDown: handleInputKeyDown,
    };
    var pickerProps = {
        platform: platform,
        picker: picker,
        onCalendarClose: closeCalendar,
        onPickerClick: handlePickerClick,
        wrapperHandlers: {
            onKeyDown: inputDisabled ? undefined : handleKeyDown,
            onClick: inputDisabled ? undefined : handleClick,
            onFocus: inputDisabled ? undefined : handleFocus,
            onBlur: handleBlur,
        },
        minDate: minDate,
        maxDate: maxDate,
    };
    switch (view) {
        case 'date':
        case 'date-time':
            return (React.createElement(DateInput, __assign({ placeholder: view === 'date-time' ? 'ДД.ММ.ГГГГ, ЧЧ:ММ' : 'ДД.ММ.ГГГГ' }, restProps, commonProps, pickerProps, { withTime: view === 'date-time', open: open, calendarRef: calendarRef, inputWrapperRef: inputWrapperRef, ref: mergeRefs([ref, maskRef, inputRef]) })));
        case 'date-range':
            return (React.createElement(DateRangeInput, __assign({ placeholder: "\u0414\u0414.\u041C\u041C.\u0413\u0413\u0413\u0413".concat(DATE_RANGE_SEPARATOR, "\u0414\u0414.\u041C\u041C.\u0413\u0413\u0413\u0413") }, restProps, commonProps, pickerProps, { open: open, calendarRef: calendarRef, inputWrapperRef: inputWrapperRef, ref: mergeRefs([ref, maskRef, inputRef]) })));
        case 'time':
            return (React.createElement(TimeInput, __assign({ placeholder: "\u0427\u0427".concat(HOURS_MINUTES_SEPARATOR, "\u041C\u041C") }, restProps, commonProps, { ref: mergeRefs([ref, maskRef, inputRef]) })));
        default:
            throw new Error('The view prop must be specified');
    }
});

export { BaseUniversalDateInput };
